/**
 * SwipeGesture
 * Felix Kiesewetter <info@felixkiesewetter.de>
 * v1.4.1
 * 2021-10-13
 */
class SwipeGesture
{

    /**
     * SwipeGesturce constructor
     *
     * @param {node|null} node
     * @param {object} options
     */
    constructor(node=null, options={})
    {
        this.node = node;
        this.setSettings(options);
        this.reset();
        this.init();
    }

    /**
     * Initialise touch and mouse events
     */
    init()
    {
        const styleSetNode = 'user-drag: none; -webkit-user-drag: none; user-select: none; -moz-user-select: none; -webkit-user-select: none; -ms-user-select: none;';
        const styleSetDraggable = `${styleSetNode} -webkit-touch-callout: none; -ms-touch-action: none; touch-action: none; pointer-events: none`;

        this.settings.mouse && this.node.addEventListener('mousedown', (e) => this.start(e), false);
        this.node.addEventListener('touchstart', (e) => this.start(e), { passive: false, capture : true });
        this.settings.mouse ? this.node.addEventListener('mousemove', (e) => this.move(e), false) : null;
        this.node.addEventListener('touchmove', (e) => this.move(e), { passive: false, capture : true });
        this.settings.mouse ? this.node.addEventListener('mouseup', (e) => this.release(e), false) : null;
        this.node.addEventListener('touchend', (e) => this.release(e), { passive: false, capture : true });
        this.node.setAttribute('style', styleSetNode);

        for (const draggable of this.node.querySelectorAll('img, a')) {
            draggable.setAttribute('style', draggable.getAttribute('style') + '; ' + styleSetDraggable);
            draggable.setAttribute('draggable', false);
            draggable.setAttribute('unselectable', 'on');
            draggable.addEventListener('dragstart', (e) => { e.preventDefault(); return false; });
        }
    }

    /**
     * Start a swipe
     *
     * @param {event} e
     * @returns {boolean}
     */
    start(e)
    {
        const event = e.originalEvent || e;
        if (this.inProgress) {
            return false;
        }
        this.inProgress = true;
        this.startX = this.getMousePosition(event).client.x || event.touches[0].pageX;
        this.lastCurrentX = this.startX;
        this.interval = setInterval(() => {
                this.duration += 10;
            }, 10
        );
        if (typeof this.settings.onStart === 'function') {
            this.settings.debug && console.log('[SwipeGesture]', 'start');
            this.settings.onStart();
        }
    }

    /**
     * Move/drag on the node
     *
     * @param {event} e
     */
    move(e)
    {
        e.preventDefault();
        const event = e.originalEvent || e;
        if (!this.inProgress) {
            return;
        }
        this.currentX = this.getMousePosition(event).client.x || event.touches[0].pageX;
        this.distance = Math.round(Math.abs(this.currentX - this.startX));
        this.direction = this.currentX < this.lastCurrentX ? (this.settings.inverseX ? -1 : 1) : (this.settings.inverseX ? 1 : -1);
        if (typeof this.settings.onMove === 'function') {
            this.settings.debug && console.log('[SwipeGesture]', 'move');
            this.settings.onMove({
                distance: this.distance,
                duration: this.duration,
                direction: this.direction
            });
        }
        this.lastCurrentX = this.currentX;
    }

    /**
     * Release swipe
     *
     * @param e
     */
    release(e) {
        if (!this.inProgress) {
            console.log('ERROR CATCH');
            return;
        }
        let fire = false;
        const speed = this.distance / this.duration;
        if (speed > this.settings.speedThreshold) {
            fire = true;
        }
        if (!fire && this.distance >= 1) {
            if (this.distance >= this.node.offsetWidth * this.settings.distanceThreshold) {
                fire = true;
            }
        }
        if (!fire) {
            this.timeout = setTimeout(this.cancel, 1000);
        }
        if (typeof this.settings.onRelease === 'function') {
            this.settings.debug && console.log('[SwipeGesture]', 'release');
            this.settings.onRelease({
                distance: this.distance,
                direction: this.direction,
                fire: fire,
                speed: speed || 0
            });
        }
        this.reset();
    }

    /**
     * Reset variables
     */
    reset()
    {
        this.settings.debug && console.log('[SwipeGesture]', 'reset');
        this.inProgress = false;
        this.startX = 0;
        this.currentX = 0;
        this.distance = 0;
        this.duration = 0;
        clearTimeout(this.timeout);
        clearInterval(this.interval);
    }

    /**
     * Set settings
     *
     * @param options
     */
    setSettings(options)
    {
        const defaultSettings = {
            debug: false,
            mouse: true,
            speedThreshold: 1,
            distanceThreshold: .5,
            inverseX: false,
            onStart: () => {
                // do stuff
            },
            onMove: (params) => {
                // do stuff
            },
            onRelease: (params) => {
                // do stuff
            },
        };
        this.settings = {...defaultSettings, ...options};
        this.settings.debug && console.log('[SwipeGesture]', this.settings);
    }

    /**
     * @param {Event} evt
     * @return {Object}
     */
    getMousePosition(event) {

        let pageX = event.pageX;
        let pageY = event.pageY;
        if (pageX === undefined) {
            pageX = event.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
            pageY = event.clientY + document.body.scrollTop + document.documentElement.scrollTop;
        }
        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;
        return {
            client: { x: event.clientX, y: event.clientY }, // relative to the viewport
            screen: { x: event.screenX, y: event.screenY }, // relative to the physical screen
            offset: { x: offsetX, y: offsetY },             // relative to the event target
            page:   { x: pageX, y: pageY }                  // relative to the html document
        }
    }

}

export default SwipeGesture;